// src/utils/push.ts
//
// Bật/tắt thông báo đẩy (Web Push qua FCM) cho MỘT máy của MỘT người.
//
// Vì sao cần: chuông trong app chỉ sáng khi app đang mở. Thông báo đẩy đi thẳng
// vào màn hình khoá, kể cả khi đã đóng app - phần gửi nằm ở
// functions/src/notify.ts (Cloud Functions), KHÔNG còn là script chạy theo giờ.
//
// Quyền thông báo là THEO TỪNG TRANG (origin), không phải bật cho cả trình
// duyệt: cho phép nhà48 thì chỉ nhà48 gửi được, mọi trang khác vẫn phải xin
// riêng. Cài app ra màn hình chính rồi thì nó còn tách hẳn thành một mục riêng
// trong cài đặt thông báo của máy, bật tắt độc lập với trình duyệt.
//
// Một token = một trình duyệt trên một máy. Cùng một người dùng điện thoại và
// máy tính sẽ có hai token, nên DB lưu theo dạng tập:
//
//     pushTokens/{emailKey}/{token} = {createdAt, updatedAt, userAgent}
//
// Token có thể tự đổi bất cứ lúc nào (trình duyệt dọn dẹp, cài lại app...). Nên
// mỗi lần vào app mà quyền vẫn còn thì gọi syncPushToken() ghi đè lại; token
// chết do bộ gửi tự dọn khi FCM báo `registration-token-not-registered`.

import {getMessaging, getToken, deleteToken, isSupported} from 'firebase/messaging';
import {get, ref, remove, set} from 'firebase/database';
import {app, db, firebaseConfig} from './firebaseConfig';
import {sanitizeEmail} from './roles';

/** Khoá Web Push của project: Firebase Console -> Cloud Messaging -> Web Push certificates */
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/** Nhớ token của máy này để lúc tắt còn biết xoá dòng nào trong DB */
const TOKEN_STORAGE_KEY = 'nha48:pushToken';

export type PushState =
    | 'unsupported'   // trình duyệt không làm được (hoặc iOS chưa cài ra màn hình chính)
    | 'unconfigured'  // thiếu REACT_APP_FIREBASE_VAPID_KEY
    | 'denied'        // người dùng đã chặn - phải vào cài đặt máy mở lại
    | 'default'       // chưa hỏi bao giờ
    | 'granted';      // đang bật

const readStoredToken = (): string | null => {
    try {
        return window.localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
};

const writeStoredToken = (token: string | null) => {
    try {
        if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
        else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
        // Trình duyệt chặn localStorage: chấp nhận, chỉ là lúc tắt phải dựa vào
        // token mà getToken() trả lại chứ không có sẵn.
    }
};

/**
 * Đăng ký service worker, đính cấu hình Firebase vào query string.
 *
 * public/firebase-messaging-sw.js là file tĩnh nên không có process.env; đây là
 * chỗ duy nhất biết đang chạy project nào (dev hay prod).
 */
const registerWorker = async (): Promise<ServiceWorkerRegistration> => {
    const config = encodeURIComponent(JSON.stringify(firebaseConfig));
    const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?config=${config}`);

    /* PHẢI chờ worker HOẠT ĐỘNG rồi mới trả về.
       ------------------------------------------------------------------
       `register()` resolve ngay khi bản đăng ký được nhận, lúc đó worker
       thường còn đang 'installing'. Nhưng getToken() bên trong gọi
       pushManager.subscribe(), thứ bắt buộc phải có worker đang hoạt động -
       không có thì nó ném lỗi.

       Nghĩa là trên một máy SẠCH, lần bấm đầu tiên luôn hỏng: người dùng thấy
       hộp xin quyền, bấm Đồng ý, rồi không có gì xảy ra nữa và trong DB không
       có token nào. Đúng triệu chứng đã gặp trên cả điện thoại lẫn máy tính.

       Hết 15 giây thì báo lỗi đọc được, thay vì để nút quay vòng mãi. */
    if (!registration.active) {
        await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_resolve, reject) => {
                setTimeout(() => reject(new Error('service worker không khởi động được sau 15 giây')), 15_000);
            }),
        ]);
    }

    return registration;
};

/** Trình duyệt này có làm được push không, và người dùng đang ở trạng thái nào */
export const getPushState = async (): Promise<PushState> => {
    if (!(await isSupported().catch(() => false))) return 'unsupported';
    if (!VAPID_KEY) return 'unconfigured';
    return Notification.permission as PushState;
};

/**
 * Máy này đã THẬT SỰ có token trong DB chưa.
 *
 * Hỏi DB chứ không hỏi trình duyệt, vì hai thứ đó lệch nhau được: quyền vẫn là
 * 'granted' mà getToken() hỏng (service worker chết, khoá VAPID của project
 * khác, máy Android thiếu Google Play services) thì `pushTokens` trống trơn -
 * trong khi Notification.permission vẫn báo 'granted'. Lấy quyền làm câu trả
 * lời cho "đang bật chưa" là nút bấm tự tin báo "Đang bật" suốt, còn bộ gửi thì
 * không có chỗ nào để bắn tới.
 */
export const isPushRegistered = async (email: string): Promise<boolean> => {
    const token = readStoredToken();
    if (!token) return false;
    try {
        const snapshot = await get(ref(db, `pushTokens/${sanitizeEmail(email)}/${token}`));
        return snapshot.exists();
    } catch {
        return false;
    }
};

/**
 * Hỏi quyền rồi lưu token. BẮT BUỘC gọi từ trong một cú bấm thật của người dùng
 * - iOS chỉ cho xin quyền khi có thao tác trực tiếp, gọi lúc mở app là bị chặn
 * thẳng, mà chặn rồi thì phải vào Cài đặt máy mới mở lại được.
 */
export const enablePush = async (email: string): Promise<PushState> => {
    const state = await getPushState();
    if (state === 'unsupported' || state === 'unconfigured' || state === 'denied') return state;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission as PushState;

    const registration = await registerWorker();
    const token = await getToken(getMessaging(app), {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
    });
    if (!token) return 'default';

    await set(ref(db, `pushTokens/${sanitizeEmail(email)}/${token}`), {
        createdAt: Date.now(),
        updatedAt: Date.now(),
        // Để lúc gỡ máy cũ trong Cài đặt còn biết đâu là điện thoại, đâu là laptop
        userAgent: navigator.userAgent.slice(0, 200),
    });
    writeStoredToken(token);
    return 'granted';
};

/**
 * Làm mới token mỗi lần vào app, khi quyền vẫn còn.
 *
 * Không hỏi quyền, không hiện gì - token đổi âm thầm thì đây là chỗ duy nhất
 * phát hiện ra. Lỗi thì nuốt: người dùng không làm gì sai để phải nhận báo lỗi.
 */
export const syncPushToken = async (email: string): Promise<void> => {
    try {
        if ((await getPushState()) !== 'granted') return;

        const registration = await registerWorker();
        const token = await getToken(getMessaging(app), {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration,
        });
        if (!token) return;

        const stored = readStoredToken();
        // Token cũ còn nằm lại trong DB thì bộ gửi sẽ bắn trùng cho tới khi FCM
        // báo nó chết - dọn ngay ở đây rẻ hơn.
        if (stored && stored !== token) {
            await remove(ref(db, `pushTokens/${sanitizeEmail(email)}/${stored}`)).catch(() => undefined);
        }

        await set(ref(db, `pushTokens/${sanitizeEmail(email)}/${token}`), {
            createdAt: Date.now(),
            updatedAt: Date.now(),
            userAgent: navigator.userAgent.slice(0, 200),
        });
        writeStoredToken(token);
    } catch {
        // Không có push thì app vẫn chạy đủ chức năng, chuông trong app vẫn chạy.
    }
};

/**
 * Tắt trên máy này. Không thu hồi được quyền của trình duyệt (chỉ người dùng
 * làm được trong cài đặt), nhưng xoá token là bộ gửi hết chỗ để bắn tới.
 */
export const disablePush = async (email: string): Promise<void> => {
    const stored = readStoredToken();
    if (stored) {
        await remove(ref(db, `pushTokens/${sanitizeEmail(email)}/${stored}`)).catch(() => undefined);
    }
    writeStoredToken(null);
    await deleteToken(getMessaging(app)).catch(() => undefined);
};
